import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import {
  Button,
  ButtonGroup,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  PopoverHeader,
  FormControl,
  FormErrorMessage,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FileTextIcon, MoreHorizontalIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDukeTiptapEditor } from '../../../useDukeTiptapEditor';

function PdfDialogReactComponent(props) {
  const { uploadFn } = useDukeTiptapEditor();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadFailed, setUploadFailed] = useState<boolean>(false);

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file === null) {
        return;
      }

      async function upload(file: File) {
        setUploading(true);

        if (uploadFn !== undefined) {
          try {
            const { filename, fileDrn } = await uploadFn(file);
            const currentPos = props.getPos();
            const pdfUrl = `/files/${filename}`;
            const title = file.name;

            props.deleteNode();
            props.editor.commands.setPDF({ href: pdfUrl, title, fileDrn }, currentPos);
          } catch (e) {
            setUploadFailed(true);
          } finally {
            setUploading(false);
          }
        }
      }

      upload(file);
    },
    [props, uploadFn],
  );

  if (!props.editor.options.editable) {
    return null;
  }

  return (
    <ButtonGroup size="xl" isAttached variant="outline" colorScheme="gray" w="full">
      <Popover matchWidth placement="bottom-start">
        <PopoverTrigger>
          <Button flex={1} justifyContent="start" leftIcon={<FileTextIcon />}>
            Add PDF file
          </Button>
        </PopoverTrigger>
        <PopoverContent w="full">
          <PopoverCloseButton />
          <PopoverHeader>Select and upload a PDF file</PopoverHeader>
          <PopoverBody>
            <Tabs isFitted>
              <TabList>{uploadFn !== undefined && <Tab>Upload</Tab>}</TabList>
              <TabPanels>
                {uploadFn !== undefined && (
                  <TabPanel>
                    <Flex direction="column" gap={4} justifyContent="center">
                      {uploading ? (
                        <Flex
                          direction="column"
                          gap={4}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Spinner size="md" color="brand.500" />
                          <Text size="sm">Uploading...</Text>
                        </Flex>
                      ) : (
                        <>
                          <FormControl isInvalid={uploadFailed}>
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            />
                            <FormErrorMessage>Failed to upload file</FormErrorMessage>
                          </FormControl>
                          <Button onClick={() => handleFileChange(file)} size="sm">
                            Upload
                          </Button>
                        </>
                      )}
                    </Flex>
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<Icon as={MoreHorizontalIcon} />}
          aria-label="image dialog options"
        />
        <MenuList>
          <MenuItem onClick={props.deleteNode}>Delete</MenuItem>
        </MenuList>
      </Menu>
    </ButtonGroup>
  );
}

function PdfDialogTiptapWrapper(props) {
  return (
    <NodeViewWrapper>
      <PdfDialogReactComponent {...props} />
    </NodeViewWrapper>
  );
}

export const PdfDialog = Node.create({
  name: 'pdf-dialog',

  draggable: true,

  group: 'block',

  atom: true,

  parseHTML() {
    return [
      {
        tag: 'pdf-dialog',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['pdf-dialog', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) => <PdfDialogTiptapWrapper {...props} />);
  },
});
